import React, { useMemo, useRef } from 'react';
import SimpleVector from './SimpleVector';
import { Group, MathUtils, Vector3 } from 'three';
import JokeSphere from './JokeSphere';
import VectorLabel from './VectorLabel';
import useDefaultValue from '../hooks/useDefaultValue';
import { useFrame, useThree } from '@react-three/fiber';
import { GroupProps } from '@react-three/fiber/dist/declarations/src/three-types';
import { Vector3OrArray } from '../../../types';

export type SimpleSphereProps = GroupProps & {
  arrowOrigin: Vector3OrArray,
  sphereCenter: Vector3OrArray,
  sphereTransition?: number,
  sphereSize: number,
  sphereOutline?: number,
  flipSide?: number,
  color: number,
  label: string,
  children?: React.ReactNode,
};

const o = new Vector3();

export default function SimpleSphere({
  arrowOrigin,
  sphereTransition,
  sphereCenter,
  sphereSize,
  sphereOutline,
  label,
  flipSide,
  children,
  color,
  ...props
}: SimpleSphereProps): JSX.Element
{
  const _sphereTransition = useDefaultValue(sphereTransition, 1);
  const labelRef = useRef<Group>(null);
  const { camera } = useThree();
  const dotSize = MathUtils.lerp(0.008, 0.004, _sphereTransition);
  const _sphereSize = useMemo(() => sphereSize * _sphereTransition, [ sphereSize, _sphereTransition ]);

  useFrame(() =>
  {
    if (!labelRef.current) return;
    const v = labelRef.current.position;
    if (Array.isArray(sphereCenter)) {
      v.fromArray(sphereCenter);
    }
    else {
      v.copy(sphereCenter);
    }

    o.setFromMatrixColumn(camera.matrixWorld, 1);

    // add the sphere itself, as well as the possible transition
    v.addScaledVector(o, _sphereSize + _sphereTransition * 0.05);
  });

  return (<group {...props} visible={sphereSize > 0.0}>
    <SimpleVector position={arrowOrigin} color={color} target={sphereCenter} dotSize={dotSize}/>
    <VectorLabel labelAlign={[ (_sphereTransition - 1.0) * (flipSide || 1), 0 ]} fontSize={15} bgColor={color}
      textColor={0xffffff} text={label}
      height={0.07} ref={labelRef} bgAlpha={1.0}
      font="Nexa"/>
    <JokeSphere color={color} position={sphereCenter} size={_sphereSize} outline={sphereOutline}>
      {children}
    </JokeSphere>
  </group>);
}
